import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import { serializer } from './serializer';
import { provide, watch } from 'vue';
import { getValidPhone } from './phoneUpdate/controller';
import { EVENTS as PHONE_EVENTS, PHONE_UPDATE } from './phoneUpdate/constants';

bus.on(constants.EVENTS.SERVICES.OPTIONS.SELECT, async (service) => {
  prepareBookmakerId(service);
});

bus.on(constants.EVENTS.SERVICES.OPTIONS.SELECTED, async (operation) => {
  const service = constants.SERVICES.SERVICES_LIST.find(
    (service) => service.id == operation.serviceId
  );
  prepareBookmakerId(service);
  preparePhoneNumber();
});

function prepareBookmakerId(service) {
  const bookmaker = service.id;
  const userStore = getStore('user');
  const store = getStore('services');
  const bookmakerId =
    userStore.state.current?.metadata?.bookmakerIds?.[bookmaker];
  store.state.transactionInitiation.bookmakerIdToUse = bookmakerId || '';
}

async function preparePhoneNumber() {
  const allowedCountries = PHONE_UPDATE.allowedCountries || [];

  const userCountry = getStore('user')?.state?.current?.countryCode || '';
  const isCountryAllowed = allowedCountries.includes(userCountry);

  if (!PHONE_UPDATE.enablePhoneValidation || !isCountryAllowed) return;

  const store = getStore('services');
  const res = await getValidPhone(
    store.state.transactionInitiation.phoneNumber
  );
  if (res.isValidated) return;

  // Si pas validé, on essaie de proposer un numéro déjà validé s'il existe
  const suggested = res.existingPhones?.[0];
  if (suggested) {
    store.state.transactionInitiation.phoneNumber =
      suggested.phone?.replace(/^\d{3}/, '') ||
      store.state.transactionInitiation.phoneNumber;
    store.state.transactionInitiation.phoneOperator =
      suggested.carrier || store.state.transactionInitiation.phoneOperator;
  }
}

async function validatePhone() {
  const allowedCountries = PHONE_UPDATE.allowedCountries || [];

  const userCountry = getStore('user')?.state?.current?.countryCode || '';
  const isCountryAllowed = allowedCountries.includes(userCountry);

  if (!PHONE_UPDATE.enablePhoneValidation || !isCountryAllowed) return true;

  const store = getStore('services');
  const phone = store.state.transactionInitiation.phoneNumber;
  const operator = store.state.transactionInitiation.isWave
    ? 'WAVE'
    : store.state.transactionInitiation.phoneOperator;

  const res = await getValidPhone(phone);

  if (!res.isValidated) {
    // Déclencher la modale de validation
    store.state.phoneUpdate.newPhoneNumber = phone;
    store.state.phoneUpdate.operator = operator;
    store.state.phoneUpdate.paymentStatus = 'idle';
    bus.emit(PHONE_EVENTS.PHONE_UPDATE.START);
    return false;
  }
  return true;
}

/**
 * Vérifie si l'ID bookmaker de l'utilisateur est blacklisté avant de lancer une transaction.
 * Retourne true si autorisé, false si bloqué.
 */
async function checkBlacklist() {
  const store = getStore('services');
  const userStore = getStore('user');
  const bookmakerIdToUse = store.state.transactionInitiation?.bookmakerIdToUse;

  // Seulement pour les services avec un ID bookmaker
  if (!bookmakerIdToUse) return true;

  const countryCode = userStore.state.current?.countryCode;
  const currency = countryCode === 'GA' ? 'xaf' : 'xof';

  const url = constants.ENDPOINTS.SERVICES.CHECK_BLACKLIST.replace(
    '{id}',
    bookmakerIdToUse
  ).replace('{currency}', currency);

  const requestId = `checkBlacklist-${Date.now()}`;

  // Indiquer à l'UI que la vérification est en cours
  store.state.transactionInitiation.checkingBlacklist = true;

  return new Promise((resolve) => {
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url,
      method: 'GET',
      data: {}
    });

    const handler = (res) => {
      if (res.id !== requestId) return;
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
      store.state.transactionInitiation.checkingBlacklist = false;

      // statusCode 403 = ID blacklisté
      // Axios met les erreurs HTTP (comme 403) dans res.error (géré par network.js)
      const isForbidden =
        res?.data?.statusCode === 403 ||
        res?.statusCode === 403 ||
        res?.error?.statusCode === 403;

      if (isForbidden) {
        resolve(false);
      } else {
        resolve(true);
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);

    // Timeout de sécurité 10s : on laisse passer si pas de réponse
    setTimeout(() => {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
      store.state.transactionInitiation.checkingBlacklist = false;
      resolve(true);
    }, 10000);
  });
}

let isConfirming = false;

bus.on(constants.EVENTS.SERVICES.INITIATION.CONFIRM, async () => {
  if (isConfirming) return;
  isConfirming = true;

  try {
    // Validation du téléphone avant de continuer
    const isValid = await validatePhone();
    if (!isValid) {
      bus.emit(constants.EVENTS.SERVICES.CLOSE_SERVICE_INITIATION);
      return; // Le contrôleur phoneUpdate prend le relais
    }

    const store = getStore('services');
    const userStore = getStore('user');
    const data = store.state.transactionInitiation;

    // ── Vérification blacklist ──────────────────────────────────────────────
    const isAllowed = await checkBlacklist();
    if (!isAllowed) {
      bus.emit(constants.EVENTS.SERVICES.INITIATION.INITIATION_FAILED, {
        messageTitle: '⛔ ID bloqué',
        message:
          'Votre ID de compte est bloqué. Veuillez aller le changer dans les réglages de votre compte pour continuer.'
      });
      return;
    }
    // ────────────────────────────────────────────────────────────────────────

    if (
      !store.state.transactionInitiation.mobileMoneyOperationConfigs?.canLaunch
    ) {
      let title =
        store.state.transactionInitiation.mobileMoneyOperationConfigs
          ?.launchErrorTitle;
      let error =
        store.state.transactionInitiation.mobileMoneyOperationConfigs
          ?.launchErrorMessage;
      bus.emit(constants.EVENTS.SERVICES.INITIATION.INITIATION_FAILED, {
        messageTitle: title,
        message: error
      });
      return;
    }

    if (
      store.state.transactionInitiation.mobileMoneyOperationConfigs?.isManual &&
      store.state.transactionInitiation.mobileMoneyOperationConfigs
        ?.manualOperationInstructions
    ) {
      bus.emit(constants.EVENTS.SERVICES.INITIATION.REQUEST_MANUAL_PAYMENT);
      return;
    }

    if (data.mobileMoneyOperationConfigs?.requiresManualLaunch) {
      bus.emit(constants.EVENTS.SERVICES.INITIATION.REQUEST_MANUAL_LAUNCH);
      return;
    }

    launchOperation();
  } finally {
    isConfirming = false;
  }
});

bus.on(
  constants.EVENTS.SERVICES.INITIATION.MANUAL_INFO_SUBMITTED,
  async (data) => {
    const store = getStore('services');
    store.state.transactionInitiation.manualInfoFilled = data;
    launchOperation();
  }
);

bus.on(constants.EVENTS.SERVICES.INITIATION.OTP_SUBMITTED, async (otp) => {
  const store = getStore('services');
  store.state.transactionInitiation.otpCode = otp;
  launchOperation();
});

bus.on(constants.EVENTS.SERVICES.INITIATION.MANUAL_LAUNCH_DONE, async () => {
  launchOperation();
});

async function launchOperation() {
  const store = getStore('services');
  const userStore = getStore('user');
  const data = store.state.transactionInitiation;
  const userData = userStore.state.current;

  // Intercept for OTP if required and not already provided
  if (data.mobileMoneyOperationConfigs?.requiresOtp && !data.otpCode) {
    bus.emit(constants.EVENTS.SERVICES.INITIATION.REQUEST_OTP);
    return;
  }

  const transaction = serializer.serialize(
    { ...data },
    store.state.transactionInitiationFees,
    userData
  );
  const requestId = `initiateTransaction-${Date.now()}`;
  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: constants.ENDPOINTS.SERVICES.INITIATE,
    method: 'POST',
    data: transaction
  });

  const handler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
      const parsed = serializer.deserialize(res);
      if (!parsed?.success) {
        bus.emit(
          constants.EVENTS.SERVICES.INITIATION.INITIATION_FAILED,
          parsed
        );
        return;
      }
      bus.emit(constants.EVENTS.SERVICES.INITIATION.INITIATED, {
        data: parsed?.data,
        message: parsed?.message,
        transaction
      });
      console.log('reqeuest from service controller', data);
      // Si la transaction est de type Wave, démarrer le polling du lien de paiement
      if (data.isWave) {
        const transactionId =
          parsed?.data?.id || parsed?.data?._id || transaction?.id;
        if (transactionId) {
          startWavePaymentLinkPolling(transactionId);
        }
      }
    }
  };
  bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
}

// --- WAVE PAYMENT LINK POLLING ---

let wavePollingInterval = null;
let wavePollingCount = 0;
const WAVE_POLL_MAX = 30; // 30 × 10s = ~5 minutes

function startWavePaymentLinkPolling(transactionId) {
  wavePollingCount = 0;
  if (wavePollingInterval) clearInterval(wavePollingInterval);

  bus.emit(
    constants.EVENTS.SERVICES.INITIATION.WAVE_PAYMENT_LINK_POLLING_START
  );

  wavePollingInterval = setInterval(() => {
    wavePollingCount++;
    if (wavePollingCount >= WAVE_POLL_MAX) {
      clearInterval(wavePollingInterval);
      wavePollingInterval = null;
      bus.emit(constants.EVENTS.SERVICES.INITIATION.WAVE_PAYMENT_LINK_TIMEOUT);
      return;
    }

    const requestId = `wave-poll-${Date.now()}`;
    const url = constants.ENDPOINTS.SERVICES.GET_TRANSACTION_DETAIL.replace(
      '{id}',
      transactionId
    );
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url,
      method: 'GET'
    });

    const handler = (res) => {
      if (res.id !== requestId) return;
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
      const detail = serializer.deserializeTransactionDetail(res);
      if (detail?.paymentLink) {
        clearInterval(wavePollingInterval);
        wavePollingInterval = null;
        bus.emit(constants.EVENTS.SERVICES.INITIATION.WAVE_PAYMENT_LINK_FOUND, {
          paymentLink: detail.paymentLink
        });
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
  }, 10000);
}

bus.on(constants.EVENTS.SERVICES.CLOSE_OPERATION_INITIATION, () => {
  if (wavePollingInterval) {
    clearInterval(wavePollingInterval);
    wavePollingInterval = null;
  }
});

bus.on(constants.EVENTS.SERVICES.INITIATION.SUBMIT_FORM, async () => {
  const store = getStore('services');
  await new Promise((r) => setTimeout(r, 100));
  if (
    store.state.transactionInitiation?.service?.bookmaker &&
    !store.state.transactionInitiation?.bookmakerIdToUse
  ) {
    bus.emit(constants.EVENTS.BOOKMAKER.REQUEST_ACCOUNT_CONFIRMATION, {
      bookmaker: store.state.transactionInitiation?.service?.id,
      bookmakerId: store.state.transactionInitiation?.bookmakerId
    });
    return;
  }

  bus.emit(constants.EVENTS.SERVICES.INITIATION.CONFIRM);
});

async function makeFeeCalculation(isSrcChange) {
  const id = window.lastFeeCalculationId;
  const store = getStore('services');
  const userStore = getStore('user');
  store.state.transactionInitiationFees = { loadingFees: true };
  const data = store.state.transactionInitiation;
  const feesRes = await calculatePaymentFees(
    data,
    userStore.state.current,
    isSrcChange
  );
  console.log('🚀 ~ feesRes:', feesRes);
  if (id !== window.lastFeeCalculationId) return;
  if (!feesRes.success) {
    store.state.initiationError =
      feesRes.message || 'Echec de calcul des frais';
    store.state.transactionInitiationFees = null;
  } else {
    store.state.transactionInitiationFees = feesRes.data;
    if (isSrcChange) {
      store.state.transactionInitiation.recipientAmount =
        store.state.transactionInitiationFees.dstAmount;
    } else {
      store.state.transactionInitiation.senderAmount =
        store.state.transactionInitiationFees.srcAmount;
    }
  }
}

function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

bus.on(constants.EVENTS.APP.READY, () => {
  if (window.initiatedLiveFeeCalculation) return;
  window.initiatedLiveFeeCalculation = true;
  const store = getStore('services');

  const debouncedFeeCalc = debounce(async (isSrcChange) => {
    makeFeeCalculation(isSrcChange);
  }, 500);

  watch(
    () => [
      `${store?.state?.transactionInitiation?.senderAmount}`,
      `${store?.state?.transactionInitiation?.recipientAmount}`,
      `${store?.state?.transactionInitiation?.paymentMethod?.id}`,
      `${store?.state?.transactionInitiation?.recipientMethod?.id}`,
      `${store?.state?.transactionInitiation?.service?.id}`,
      `${store?.state?.transactionInitiation?.operation?.id}`
      // `${store?.state?.transactionInitiation?.phoneNumber}`,
      // `${store?.state?.transactionInitiation?.phoneOperator}`,
      // `${store?.state?.transactionInitiation?.isWave}`
    ],
    (newVal, oldVal) => {
      const [
        newSrcAmount,
        newDstAmount,
        newSrcMethod,
        newDstMethod,
        newService,
        newOperation
        // newPhone,
        // newOperator,
        // newIsWave
      ] = newVal;
      const [
        oldSrcAmount,
        oldDstAmount,
        oldSrcMethod,
        oldDstMethod,
        oldService,
        oldOperation
        // oldPhone,
        // oldOperator,
        // oldIsWave
      ] = oldVal;
      const fees = store.state.transactionInitiationFees;

      const amountChanged =
        oldSrcAmount != newSrcAmount || oldDstAmount != newDstAmount;
      const methodChanged =
        oldSrcMethod != newSrcMethod || oldDstMethod != newDstMethod;
      const serviceChanged = oldService != newService;
      const operationChanged = oldOperation != newOperation;
      // const phoneChanged = oldPhone != newPhone || oldOperator != newOperator || oldIsWave != newIsWave;

      if (
        !amountChanged &&
        !methodChanged &&
        !serviceChanged &&
        !operationChanged //&&
        // !phoneChanged
      )
        return;

      if (
        amountChanged &&
        fees &&
        fees.srcAmount == newSrcAmount &&
        fees.dstAmount == newDstAmount
      )
        return;

      const isSrcChange = newSrcAmount != oldSrcAmount;
      store.state.initiationError = null;
      store.state.transactionInitiationFees = { loadingFees: true };
      window.lastFeeCalculationId = Date.now();
      debouncedFeeCalc(isSrcChange);
    },
    { deep: true }
  );
});

bus.on(constants.EVENTS.APP.READY, () => {
  if (window.phoneWatcherInitiated) return;
  window.phoneWatcherInitiated = true;
  const store = getStore('services');

  const debouncedFeeCalc = debounce(async (isSrcChange) => {
    makeFeeCalculation(isSrcChange);
  }, 500);

  watch(
    () => [store?.state?.transactionInitiation?.phoneNumber],
    (newVal) => {
      const phone = store?.state?.transactionInitiation?.phoneNumber;
      if (phone) {
        store.state.transactionInitiation.phoneNumber = `${phone}`.trim();
      }
    },
    { deep: true }
  );
});

bus.on(constants.EVENTS.APP.READY, () => {
  if (window.mobileMoneyOperationConfigWatcherInitiated) return;
  window.mobileMoneyOperationConfigWatcherInitiated = true;
  const store = getStore('services');

  watch(
    () => [
      `${store?.state?.transactionInitiation?.senderAmount}`,
      `${store?.state?.transactionInitiation?.recipientAmount}`,
      `${store?.state?.transactionInitiation?.phoneOperator}`,
      `${store?.state?.transactionInitiation?.operation?.id}`,
      `${store?.state?.transactionInitiation?.service?.id}`,
      `${store?.state?.transactionInitiation?.isWave}`
    ],
    () => {
      const userStore = getStore('user');
      const appDataStore = getStore('appData');

      store.state.transactionInitiation.mobileMoneyOperationConfigs =
        getMobileMoneyOperationConfig(
          store?.state?.transactionInitiation,
          userStore?.state?.current,
          appDataStore?.state?.data
        );
    },
    { deep: true }
  );
});

bus.on(constants.EVENTS.APP.READY, () => {
  if (window.initiatedLiveAmountConvertion) return;
  window.initiatedLiveAmountConvertion = true;
  const store = getStore('services');

  const debouncedConvertAmount = debounce(async (isSrcChange) => {
    makeAmountConversion(isSrcChange);
  }, 500);

  watch(
    () => [
      `${store?.state?.transactionInitiation?.recipientAmountOriginalCurrency}`,
      `${store?.state?.transactionInitiation?.senderAmountOriginalCurrency}`,
      `${store?.state?.transactionInitiation?.service?.id}`,
      `${store?.state?.transactionInitiation?.senderAmount}`
    ],
    (newVal, oldVal) => {
      const [newDst, newSrc, newServiceId] = newVal;
      const [oldDst, oldSrc, oldServiceId] = oldVal;
      if (newServiceId !== 'usdt') return;
      store.state.transactionInitiation.convertingAmount = true;
      let isSrcChange = newSrc != oldSrc;
      debouncedConvertAmount(isSrcChange);
    },
    { deep: true }
  );
});

async function makeAmountConversion(isSrcChange) {
  const store = getStore('services');
  const userStore = getStore('user');
  const appDataStore = getStore('appData');

  const userCountry = userStore.state?.current?.countryCode;
  store.state.transactionInitiation.convertingAmount = true;

  let userCurrency = 'XOF';
  if (userCountry == 'GA') userCurrency = 'XAF';
  let conversionRate = 0; // Valeur par défaut
  const countryRates = appDataStore.state?.data?.rates?.[userCountry];
  let rates = null;
  console.log(
    '🚀 ~ makeAmountConversion ~ store.state.transactionInitiation?.operation:',
    store.state.transactionInitiation?.operation
  );
  if (store.state.transactionInitiation?.operation?.id == 'sell')
    rates = countryRates?.sellRates;
  else if (store.state.transactionInitiation?.operation?.id == 'buy')
    rates = countryRates?.buyRates;

  if (rates?.USDT?.[userCurrency]) {
    conversionRate = rates.USDT?.[userCurrency];
  }

  if (!isSrcChange) {
    const originalAmount =
      store.state.transactionInitiation.recipientAmountOriginalCurrency || 0;
    const convertedAmount = Math.ceil(originalAmount * conversionRate);
    store.state.transactionInitiation.recipientAmount = convertedAmount;
  } else {
    const originalAmount =
      store.state.transactionInitiation.senderAmountOriginalCurrency || 0;
    const convertedAmount = Math.ceil(originalAmount * conversionRate);
    store.state.transactionInitiation.senderAmount = convertedAmount;
  }
  store.state.transactionInitiation.convertingAmount = false;
}

function getMobileMoneyOperationConfig(raw, userData, appConfigs) {
  const key = `${raw.operation?.id}:${raw.service?.id}`;
  const operationKeys = {
    'fund:1xbet': 'fund1xBet',
    'withdraw:1xbet': 'withdrawal1xBet',
    'fund:betwinner': 'fundBetWinner',
    'withdraw:betwinner': 'withdrawalBetWinner',
    'fund:1win': 'fund1Win',
    'withdraw:1win': 'withdrawal1Win',
    'fund:melbet': 'fundMelbet',
    'withdraw:melbet': 'withdrawalMelbet',
    'fund:eightStarz': 'fundEightStarz',
    'withdraw:eightStarz': 'withdrawalEightStarz',
    'buy:usdt': 'buyUsdt',
    'sell:usdt': 'sellUsdt'
  };
  const operation = operationKeys[key];
  const countryCode = userData?.countryCode;

  // 1. Check if the service is enabled for this country
  const isServiceEnabled =
    appConfigs.serviceAvailability?.[countryCode]?.[operation] ?? true;
  if (!isServiceEnabled) {
    return {
      provider: 'disabled',
      canLaunch: false,
      isManual: false,
      manualOperationInstructions: null,
      launchErrorTitle: 'Service indisponible dans votre pays',
      launchErrorMessage:
        "Ce service est temporairement désactivé dans votre région. Contactez-nous pour plus d'informations."
    };
  }

  const operator = raw.phoneOperator;
  let operatorCheck = operator;
  if (raw.isWave) {
    operatorCheck = 'WAVE';
  } else {
    if (/sbin/i.test(operatorCheck)) operatorCheck = 'CELTIS';
    if (/celtiis/i.test(operatorCheck)) operatorCheck = 'CELTIS';
    if (/LIBERTIS/i.test(operatorCheck)) operatorCheck = 'MOOV';
    if (/(togocel)|(tmoney)|(togo ?telecom)/i.test(operatorCheck))
      operatorCheck = 'TMONEY';
    if (/togoceil|togocel|togocel2|togo telecom/i.test(operatorCheck))
      operatorCheck = 'TMONEY';
    if (/mtn/i.test(operatorCheck)) operatorCheck = 'MTN';
    if (/moov|onatel|Libertis/i.test(operatorCheck)) operatorCheck = 'MOOV';
    if (/airtel/i.test(operatorCheck)) operatorCheck = 'AIRTEL';
    if (/(orange)/i.test(operatorCheck)) operatorCheck = 'ORANGE';
    if (/(free)/i.test(operatorCheck)) operatorCheck = 'FREE';
  }
  let provider =
    appConfigs.paymentProviders?.[countryCode]?.[operation]?.[operatorCheck] ||
    'none';

  const configs = {
    provider,
    manualOperationInstructions: null,
    canLaunch: provider !== 'none',
    isManual: provider === 'manual',
    requiresOtp:
      appConfigs.otpSettings?.[countryCode]?.[operation]?.[operatorCheck] ||
      false,
    launchErrorTitle:
      provider == 'none' ? `Réseau ${operatorCheck} bientot disponible` : '',
    launchErrorMessage:
      provider == 'none'
        ? `Service de paiement mobile par ${operatorCheck} ne sont pas encore disponible. Veuilllez nous contacter si cela persiste`
        : ''
  };
  if (provider == 'simpilot' && operatorCheck !== 'CELTIS') {
    configs.requiresManualLaunch = true;
  }
  if (
    (configs.isManual || configs.requiresOtp || configs.requiresManualLaunch) &&
    (raw.operation?.id == 'fund' || raw.operation?.id == 'buy')
  ) {
    const instructions = appConfigs.manualOperationsInstructions || [];
    const manualInstruction = instructions.find((instruction) => {
      let countryMatch = instruction.country == countryCode;
      let operatorMatch =
        (instruction.operator || '').toUpperCase() ==
        (operatorCheck || '').toUpperCase();
      return countryMatch && operatorMatch;
    });
    if (manualInstruction)
      configs.manualOperationInstructions = buildManualOperation(
        raw,
        manualInstruction
      );
    else {
      configs.canLaunch = false;
      configs.launchErrorTitle = `Construction en cours des paiement ${operatorCheck}`;
      configs.launchErrorMessage = `Les paiements via ${operatorCheck} sont en cours de configuration. Veuillez nous contacter si cela persiste`;
    }
  }

  return configs;
}

function buildManualOperation(transactionData, instructionsData) {
  let stringInstrucctions = JSON.stringify(instructionsData);
  stringInstrucctions = stringInstrucctions.replace(
    /montant/g,
    transactionData.senderAmount
  );
  return JSON.parse(stringInstrucctions);
}

async function calculatePaymentFees(transactionData, userData, isSrcChange) {
  const requestId = `fee-${Date.now()}`;
  const data = serializer.serializeFee(transactionData, userData, isSrcChange);
  let url = constants.ENDPOINTS.SERVICES.CALCULATE_FEES;
  url = url
    .replace(/{amount}/, data.amount)
    .replace(/{computeAmountToPay}/, data.computeAmountToPay);
  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url,
    method: 'POST',
    data
  });
  return new Promise((resolve) => {
    bus.on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
      if (res.id !== requestId) return;
      resolve(serializer.deserializeFee(res.data));
    });
  });
}
